import { reactive, ref } from "vue";
import { ServiceFromServer, ServiceOverage } from "./service";
// The lifecycle rules live in plan-state.ts (no imports, so node --test loads
// it). IMPORTED and then exported, never `export { } from` alone: formatSpecs
// below calls serviceStatusLabel, and a bare re-export creates no local binding,
// so every Service constructor would throw a ReferenceError that vite build does
// not catch. SERVICE_DELETE_AFTER_MS there is kept in sync with the backend
// DELETE_SERVICE sweep (SERVICE_DELETE_AFTER_DAYS in service_state.py, read by
// infra/admin/admin/remove_resource/index.py).
import {
    SERVICE_STATUS_LABEL,
    CONTAINED_ACTIVE_CEILING,
    SERVICE_DELETE_AFTER_MS,
    isServiceContained,
    serviceStatusLabel,
    isServiceRunning,
    isServiceSuspended,
    canManageData,
    QUOTA_SUSPEND_MIN_BYTES,
    STORED_LIMIT_KEYS,
} from "./plan-state";
import type { StoredLimitKey, StoredLimits } from "./plan-state";

export {
    SERVICE_STATUS_LABEL,
    CONTAINED_ACTIVE_CEILING,
    SERVICE_DELETE_AFTER_MS,
    isServiceContained,
    serviceStatusLabel,
    isServiceRunning,
    isServiceSuspended,
    canManageData,
};

/**
 * service.group -> the modelSpecs key. The one copy of this map: formatSpecs,
 * plan-copy.ts and the newsletter page all read it. Promo groups point at the
 * plan whose limits they carry (plan.py: 61 Standard; 62, 63 and 64 Premium),
 * and 99 is the host service, which runs without limits.
 */
export const PLAN_KEY_BY_GROUP: { [group: number]: string } = {
    1: "Free",
    2: "Standard",
    3: "Premium",
    50: "Unlimited",
    51: "Standard (Perpetual License)",
    52: "Premium (Perpetual License)",
    61: "Standard",
    62: "Premium",
    63: "Premium",
    64: "Premium",
    99: "Unlimited",
};

/** A group this build does not know reads as Unlimited rather than throwing:
 *  JSON.parse(JSON.stringify(undefined)) used to take the whole ctor down. */
export function planKeyForGroup(group: number): string {
    return PLAN_KEY_BY_GROUP[group] || "Unlimited";
}

/**
 * The stored ceilings of a plan, in bytes, for plan-state's comparisons. null
 * (unknown) until GET /plan has answered, or when any value is unreadable: the
 * built-in table is blank on purpose, and a blank limit must never read as
 * "nothing is over". A plan's "Unlimited" is null (no ceiling).
 */
export function storedLimitsForPlan(planKey: string): StoredLimits | null {
    if (!planSpecsLoaded.value) return null;
    const plan = modelSpecs[planKey];
    if (!plan?.storage || !plan?.storage_bytes) return null;
    const field: Record<StoredLimitKey, string> = { dbst: "database", fpool: "pool", emlst: "email" };
    const limits = {} as StoredLimits;
    for (const key of STORED_LIMIT_KEYS) {
        const text = plan.storage[field[key]];
        const bytes = plan.storage_bytes[field[key]];
        if (text === "Unlimited") {
            limits[key] = null;
            continue;
        }
        if (typeof bytes !== "number" || !Number.isFinite(bytes)) return null;
        limits[key] = bytes;
    }
    return limits;
}

/** The ceiling as the card prints it ("500MB", "0B", "Unlimited"). */
export function limitText(planKey: string, key: StoredLimitKey): string {
    const plan = modelSpecs[planKey];
    const field = { dbst: "database", fpool: "pool", emlst: "email" }[key];
    return plan?.storage?.[field] || "";
}

export function formatSpecs(service: ServiceFromServer) {
    function planCodeConverter(group: number) {
        return JSON.parse(JSON.stringify(modelSpecs[planKeyForGroup(group)]));
    }

    let currentPlan = planCodeConverter(service.group);

    function numberToStringWithUnits(num: number) {
        if (num === 0) return "0";
        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
        return num.toString();
    }

    // Database is DynamoDB alone (dbst). Cloud files used to be folded in here,
    // which double counted them once the file pool bar below took them.
    const databaseUsage = service.storage_info?.database || 0;

    // The file pool: hosting, cloud files and the BunnyQuery index share one
    // ceiling, so they are drawn as one bar with three coloured segments.
    const hostUsage = service.storage_info?.host || 0;
    const cloudUsage = service.storage_info?.cloud || 0;
    const fileUsage = service.storage_info?.file || 0;
    const poolUsage = hostUsage + cloudUsage + fileUsage;
    const poolLimit = currentPlan.storage.pool === "Unlimited" ? 0 : currentPlan.storage_bytes.pool;
    // Segment widths are shares of the ceiling; past the ceiling (an overage
    // plan, or a hard stop plan filled to the brim) they become shares of the
    // total so the bar stays full instead of spilling over its track.
    const scale = Number.isFinite(poolLimit) && poolLimit > 0
        ? 100 / Math.max(poolLimit, poolUsage)
        : 0;
    const segment = (bytes: number) => ({
        bytes,
        text: convertBytesToReadableStorage(bytes),
        width: `${Math.max(0, bytes * scale)}%`,
    });

    // Month counters are joined in by pull-services; a region that predates
    // them sends none, and the meter must read that as 0, not NaN.
    const egressUsage = service.storage_info?.egress || 0;
    const emailSendsUsage = service.storage_info?.email_sends || 0;
    const mauUsage = service.storage_info?.mau || 0;

    let serviceStatus = {
        id: service.projectId,
        original_service_id: service.service,
        statusCode: service.active,
        name: service.name,
        status: serviceStatusLabel(service.active),
        canChangePlan: !currentPlan.label.includes("Perpetual") && currentPlan.label !== "Unlimited",
        users_string: numberToStringWithUnits(service.users),
        users_percent: `${getPercentUsed(service.users, currentPlan.users === 0 ? 0 : currentPlan.users)}%`,
        users: service.users,
        newsletter_subscribers_string: numberToStringWithUnits(service.newsletter_subscribers),
        newsletter_subscribers: service.newsletter_subscribers,
        storage_bytes: {
            ...service.storage_info,
            database: databaseUsage,
        },
        storage: {
            database: convertBytesToReadableStorage(databaseUsage),
            database_percent: `${getPercentUsed(databaseUsage, currentPlan.storage.database === "Unlimited" ? 0 : currentPlan.storage_bytes.database)}%`,
            cloud: convertBytesToReadableStorage(service.storage_info?.cloud),
            cloud_percent: `${getPercentUsed(service.storage_info?.cloud || 0, currentPlan.storage.cloud === "Unlimited" ? 0 : currentPlan.storage_bytes.cloud)}%`,
            email: convertBytesToReadableStorage(service.storage_info?.email),
            // A plan whose served email limit is 0 is drawn against the amount
            // it suspends above instead of against 0, which would print no bar
            // at all (getPercentUsed reads a 0 ceiling as unlimited). Every plan
            // that includes email storage, Free included since owner decision 11,
            // is drawn against its real limit.
            email_percent: `${getPercentUsed(service.storage_info?.email || 0, currentPlan.storage.email === "Unlimited" ? 0 : (currentPlan.storage.email === "0B" ? QUOTA_SUSPEND_MIN_BYTES : currentPlan.storage_bytes.email))}%`,
            email_bytes: service.storage_info?.email || 0,
            // Served zero, not an unknown blank: this plan includes none.
            email_limit_zero: currentPlan.storage.email === "0B",
            host: convertBytesToReadableStorage(service.storage_info?.host),
            host_percent: `${getPercentUsed(service.storage_info?.host || 0, currentPlan.storage.host === "Unlimited" ? 0 : currentPlan.storage_bytes.host)}%`,
            file: convertBytesToReadableStorage(service.storage_info?.file),
            file_percent: `${getPercentUsed(service.storage_info?.file || 0, currentPlan.storage.file === "Unlimited" ? 0 : currentPlan.storage_bytes.file)}%`,
            pool: convertBytesToReadableStorage(poolUsage),
            pool_bytes: poolUsage,
            pool_percent: `${getPercentUsed(poolUsage, poolLimit)}%`,
            segments: {
                host: segment(hostUsage),
                cloud: segment(cloudUsage),
                file: segment(fileUsage),
            },
        },
        usage_month: {
            month: service.storage_info?.month || "",
            egress: convertBytesToReadableStorage(egressUsage),
            // egress_bytes is already 0 for "Unlimited" and NaN while the plan
            // is still unknown; getPercentUsed reads both as "no bar".
            egress_percent: `${getPercentUsed(egressUsage, currentPlan.egress_bytes)}%`,
            email_sends: groupThousands(emailSendsUsage),
            email_sends_percent: `${getPercentUsed(emailSendsUsage, Number(currentPlan.email_sends) || 0)}%`,
            mau: groupThousands(mauUsage),
        },
        overage: deriveOverage(service.ovg),
    }
    return {
        currentPlan,
        serviceStatus
    };
}

function getPercentUsed(used: number, total: number) {
    // An unknown ceiling reads as "no bar", the same as an unlimited one. The
    // table's limits are blank until GET /plan answers, and without this the
    // dashboard renders "NaN%" in that window.
    if (!Number.isFinite(total) || total === 0) return 0;
    let percent = (used / total) * 100;
    if (used > 0 && percent < 1) return 1;
    return Math.round(percent);
}

export function convertBytesToReadableStorage(bytes: number) {
    if (!bytes) return "0B";
    if (bytes >= 1024 ** 4) return (bytes / (1024 ** 4)).toFixed(2) + "TB";
    if (bytes >= 1024 ** 3) return (bytes / (1024 ** 3)).toFixed(2) + "GB";
    if (bytes >= 1024 ** 2) return (bytes / (1024 ** 2)).toFixed(2) + "MB";
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + "KB";
    return bytes + "B";
}

/** One line on a pricing card: which limit it quotes, what it is called, and the
 *  class the card puts on that line.
 *
 *  `icon` is inert in THIS client: planCards.vue renders `text` only and marks
 *  every line with the same check mark. It is carried so offer_list keeps the
 *  `{ class, text }` shape that www.skapi.com's card, which does bind the class
 *  over icon css, shares with it. `unit` is descriptive only; the value a line
 *  prints is the already-formatted string derivePlan holds for that limit, so
 *  the card and the dashboard meter cannot format the same number differently. */
type PlanDisplayRow = { limit: string; label: string; unit?: string; icon: string };

/**
 * WHICH quota lines a card shows, in what order, under what label.
 *
 * The backend owns this list now (plan.py's PLAN_DISPLAY_ROWS, served as
 * `display` by GET /plan). When the client owned it, the one limit this product
 * is named for, the BunnyQuery file storage (`dbfst`), was never pushed at all:
 * it was fetched, converted into storage_bytes.file and metered on the dashboard,
 * and no pricing card ever printed it.
 *
 * Plain, NOT reactive: it is replaced wholesale and derivePlan re-runs after the
 * replacement, so the cards redraw from offer_list, which is reactive.
 */
let planDisplayRows: PlanDisplayRow[] = [];

/**
 * Limits the plan cards leave out on this site. BunnyQuery is a trimmed down
 * Skapi: email storage, email sends and newsletter subscribers are real quotas
 * of the project, but this dashboard has no mail or newsletter page, so they are
 * not features a BunnyQuery plan sells. Skipped in derivePlan whatever list
 * GET /plan serves; the meters and the usage panel are not affected.
 */
const CARD_HIDDEN_LIMITS = new Set<string>(["emlst", "emlsd", "nlsub"]);

/**
 * The same catalogue, built in, for a response that carries no `display`.
 *
 * Not optional and not paranoia. The deployed lambda LAGS this source tree: the
 * live us-east-1 /plan answers today with `plans` and `units` and no `display`
 * key at all. And a visitor who already holds a response is served it from cache
 * for a week (max-age=604800), so "the backend sends it now" stays false for a
 * week after it becomes true.
 *
 * Keep in step with plan.py's PLAN_DISPLAY_ROWS, same rows in the same order, so
 * a card does not reshuffle its lines the moment the served catalogue arrives.
 */
const BUILT_IN_DISPLAY_ROWS: PlanDisplayRow[] = [
    // One pooled line: hosting, cloud files and the BunnyQuery index share the
    // fpool ceiling (hard stop and overage alike). Keep in step with plan.py.
    { limit: "fpool", label: "File Storage (hosting, files and BunnyQuery index)", unit: "bytes", icon: "file" },
    { limit: "dbst", label: "Database Storage", unit: "bytes", icon: "data" },
    { limit: "users", label: "User Accounts", unit: "count", icon: "user" },
    { limit: "emlst", label: "Email Storage", unit: "bytes", icon: "mail" },
    { limit: "egres", label: "Monthly Bandwidth", unit: "per_month_bytes", icon: "swap" },
    { limit: "emlsd", label: "Monthly Email Sends", unit: "per_month_count", icon: "mail" },
    // A HELD count, not a monthly one: the subscribers of every newsletter
    // group of the service, numeric and named, standing right now.
    { limit: "nlsub", label: "Newsletter Subscribers", unit: "count", icon: "mail" },
];

/** 50000 -> "50,000". Done here and not with toLocaleString so the separator
 *  cannot change with the visitor's locale while the rest of the page stays in
 *  English. */
function groupThousands(n: number): string {
    return String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** Integer cents -> "$1.23". The one money format the dashboard prints. */
function centsToDollars(cents: number): string {
    return `$${((cents || 0) / 100).toFixed(2)}`;
}

/** A per unit rate, in cents, -> "$0.023", "$0.10", "$1.00".
 *
 *  Three decimals when the rate needs them (2.3 cents is $0.023) and two
 *  otherwise, so a whole rate does not print as "$1.000". */
function rateToDollars(centsPerUnit: number): string {
    let text = ((centsPerUnit || 0) / 100).toFixed(3);
    if (text.endsWith("0")) text = text.slice(0, -1);
    return `$${text}`;
}

/** One overage rate as plan.py's OVERAGE_RATES carries it. `billed` is false for
 *  a metric the meter records but does not charge (bandwidth while
 *  BILL_EGRESS_OVERAGE is off). Absent means billed: a lambda from before the
 *  flag existed billed everything it priced. */
type OverageRate = { label: string; unit: string; cents_per_unit: number; billed?: boolean };
type OverageRates = { [metric: string]: OverageRate };

/**
 * The overage price list, built in, for a response that carries no `overage`.
 *
 * Same rule as the blank limits above, only inverted: a price is printed on
 * the card the moment it renders, so it cannot wait for GET /plan, and a
 * cached response from before the key existed keeps answering for a week.
 * Keep in step with plan.py's OVERAGE_RATES_V2, which is the authority; the
 * served copy overwrites this one in applyPlanLimits.
 */
const BUILT_IN_OVERAGE_RATES: OverageRates = {
    fpool: { label: "File pool", unit: "GB-month", cents_per_unit: 5.0, billed: true },
    dbst: { label: "Database storage", unit: "GB-month", cents_per_unit: 50.0, billed: true },
    emlst: { label: "Email storage", unit: "GB-month", cents_per_unit: 10.0, billed: true },
    // plan.py BILL_EGRESS_OVERAGE ships False: metered, not charged.
    egres: { label: "Bandwidth", unit: "GB", cents_per_unit: 12.0, billed: false },
    emlsd: { label: "Email sends", unit: "1,000 sends", cents_per_unit: 100.0, billed: true },
    nlsub: { label: "Newsletter subscribers", unit: "1,000 subscribers-month", cents_per_unit: 100.0, billed: true },
    mau: { label: "Monthly active users", unit: "1,000 users", cents_per_unit: 0.0, billed: true },
};

/** The order the dashboard lists overage rows in, and every metric it knows. A
 *  metric the server adds later is priced there and simply not drawn here. */
const OVERAGE_METRICS = ["fpool", "dbst", "emlst", "egres", "emlsd", "nlsub", "mau"];

/** Held BYTE levels are billed in GB-month, egress in GB: those four are the
 *  byte metrics on the dashboard. Email sends, newsletter subscribers and
 *  active users are counts. nlsub is a held level like the storage ones, but it
 *  is held in subscribers rather than bytes, so it belongs with the counts:
 *  listed here it would print 5,000 subscribers as "4.88KB". */
const OVERAGE_BYTE_METRICS = ["fpool", "dbst", "emlst", "egres"];

/** Plain, NOT reactive: replaced wholesale by applyPlanLimits, which then
 *  re-runs derivePlan, and the billing line it feeds lives in offer_list, which
 *  is reactive. */
let overageRates: OverageRates = BUILT_IN_OVERAGE_RATES;

/**
 * The public switches GET /plan serves (plan.get_public_switches). Absent, from a
 * cached or older response, means both false.
 *
 * Declared HERE, above modelSpecs, on purpose: the modelSpecs IIFE runs
 * derivePlan at module load, and derivePlan reads this through
 * overageRateLines. Declared below it, the first read would be a temporal
 * dead zone ReferenceError that blanks the whole app.
 *
 * Reactive, so a card or meter that reads it redraws when /plan answers.
 */
export const planSwitches = reactive({
    enforce_egress_hard_stop: false,
    bill_egress_overage: false,
});

/** Whether a metric's overage is charged: the rate says so (absent = billed)
 *  and, for bandwidth, BILL_EGRESS_OVERAGE is on. */
function overageMetricBilled(metric: string, rate?: { billed?: boolean } | null): boolean {
    if (rate && rate.billed === false) return false;
    if (metric === "egres" && !planSwitches.bill_egress_overage) return false;
    return true;
}

/** `service.ovg` -> what the Plan & Usage card prints for it. null when the
 *  OverageMeter job has not written a summary for this service yet. */
function deriveOverage(ovg: ServiceOverage | undefined) {
    if (!ovg || typeof ovg !== "object") return null;

    const use = ovg.use || {};
    const lim = ovg.lim || {};
    const priced = ovg.ovg || {};
    // The job's own rates are stored beside the numbers they priced. Prefer
    // them over the /plan copy, so a rate change does not relabel a month
    // that was billed at the old one.
    const rates = ovg.rates || {};

    const rows = OVERAGE_METRICS.map((metric) => {
        const rate = { ...(overageRates[metric] || BUILT_IN_OVERAGE_RATES[metric] || {}), ...(rates[metric] || {}) } as OverageRate;
        const isBytes = OVERAGE_BYTE_METRICS.includes(metric);
        // MAU has no limit of its own: it is measured against the user limit.
        const limit = metric === "mau" ? lim.users : lim[metric];
        const used = Number(use[metric]) || 0;
        const hasLimit = typeof limit === "number" && Number.isFinite(limit);
        const excess = hasLimit ? Math.max(0, used - (limit as number)) : 0;
        const format = isBytes ? convertBytesToReadableStorage : groupThousands;
        const units = Number(priced[metric]?.units) || 0;
        // Effective billed = the ledger's stamp AND the live rate and switch.
        const billed = priced[metric]?.billed !== false && overageMetricBilled(metric, rate);
        return {
            metric,
            label: rate.label || metric,
            used: format(used),
            included: hasLimit ? format(limit as number) : "Unlimited",
            over: format(excess),
            units: `${units.toFixed(3)} ${rate.unit || ""}`.trim(),
            cost: billed ? centsToDollars(priced[metric]?.cents || 0) : "not billed yet",
        };
    });

    return {
        month: ovg.prd || "",
        sampled: ovg.ts ? new Date(ovg.ts).toLocaleDateString() : "",
        billable: !!ovg.billable,
        // "no_subscription" -> "No subscription": the ledger's status words are
        // readable as they are, they just need a sentence case.
        bill_status: ((status: string) => status ? status[0].toUpperCase() + status.slice(1).replace(/_/g, " ") : "")(ovg.bill?.status || ""),
        total: centsToDollars(ovg.tot_cents || 0),
        rows,
    };
}

/** The overage rates a card lists under its "No hard stop" line, one short
 *  line each, priced from the rates so a rate change in plan.py is never
 *  contradicted by hand-written copy. */
function overageRateLines(rates: OverageRates, meteredBandwidth: boolean): string[] {
    const rate = (metric: string) => rates[metric] || BUILT_IN_OVERAGE_RATES[metric];
    const lines: string[] = [];
    // fpool: hosting, cloud files and the index share this one ceiling, which
    // the card prints as its File Storage line.
    lines.push(`${rateToDollars(rate("fpool").cents_per_unit)} per GB-month of file storage`);
    const dbst = rate("dbst").cents_per_unit;
    const emlst = rate("emlst").cents_per_unit;
    // Database and email storage share a rate today, so they share a line;
    // priced apart they get one each rather than a wrong shared number.
    if (dbst === emlst) {
        lines.push(`${rateToDollars(dbst)} per GB-month of database and email storage`);
    } else {
        lines.push(`${rateToDollars(dbst)} per GB-month of database storage`);
        lines.push(`${rateToDollars(emlst)} per GB-month of email storage`);
    }
    // Bandwidth is only named while the plan quotes a ceiling for it; with an
    // unlimited egres (free bandwidth) there is nothing past the limit to bill.
    // And it is only PRICED while it is actually charged: with
    // BILL_EGRESS_OVERAGE off (the served switch, absent = off) the meter records
    // it and bills nothing, so the card says so instead of quoting a rate.
    const bandwidthBilled = meteredBandwidth && overageMetricBilled("egres", rate("egres"));
    if (bandwidthBilled) lines.push(`${rateToDollars(rate("egres").cents_per_unit)} per GB of bandwidth`);
    lines.push(`${rateToDollars(rate("emlsd").cents_per_unit)} per 1,000 email sends`);
    // Newsletter subscribers are a held level, so they are billed per 1,000
    // held for the month, not per subscription event. Named only while the rate
    // is real, the same way active users are.
    const nlsub = rate("nlsub").cents_per_unit;
    if (nlsub > 0) lines.push(`${rateToDollars(nlsub)} per 1,000 newsletter subscribers-month`);
    const mau = rate("mau").cents_per_unit;
    lines.push(mau > 0 ? `${rateToDollars(mau)} per 1,000 active users` : "Active users free");
    if (meteredBandwidth && !bandwidthBilled) lines.push("Bandwidth past the allowance not billed yet");
    return lines;
}

/**
 * Turn one plan's authored fields into everything drawn from them.
 *
 * Extracted from the IIFE below so it can run TWICE: once on the built-in table
 * at module load, and again on whatever `GET /plan` returns. `storage_bytes`,
 * `users` and `offer_list` are all derived, so replacing a limit without
 * re-deriving would leave the meters and the marketing list quoting the old one.
 */
function derivePlan(item: any) {
    function storageUnitConverter(storage: string) {
        // convert "1GB" to 1073741824
        if (storage === "Unlimited") return 0;

        let unit = storage.replace(/[\d.\s]/g, "").toUpperCase(); // "GB", "TB"
        let number = parseFloat(storage.replace(/[^\d.]/g, "")); // 1

        let multiplier = ({
            B: 1,
            KB: 1024,
            MB: 1024 ** 2,
            GB: 1024 ** 3,
            TB: 1024 ** 4,
            PB: 1024 ** 5,
        })[unit];

        if (!multiplier || Number.isNaN(number)) {
            return NaN;
        }

        return number * multiplier;
    }

    {
        item.storage_bytes = {
            database: storageUnitConverter(item.storage.database),
            cloud: storageUnitConverter(item.storage.cloud),
            host: storageUnitConverter(item.storage.host),
            email: storageUnitConverter(item.storage.email),
            file: storageUnitConverter(item.storage.file),
            // the pooled ceiling the three file buckets share
            pool: storageUnitConverter(item.storage.pool),
        }
        // Metered per MONTH, unlike everything above, which is a level held. Read
        // through the same converter so "Unlimited" lands on 0 and drops its line,
        // exactly as the storage figures do.
        item.egress_bytes = storageUnitConverter(item.egress || "Unlimited");
        function convertUsers(users: number | string) {
            if (typeof users === "number") return users;
            if (typeof users === "string") {
                // Grouping commas out ("50,000"), and a plain number only when the WHOLE
                // string is one: parseFloat alone reads "50K" as 50 and "50,000" as 50,
                // which drew a full Users meter for any project past 50 users.
                let text = users.trim().replace(/,/g, "");
                if (/^\d+(\.\d+)?$/.test(text)) return parseFloat(text);

                if (text.toLowerCase() === "unlimited") return 0;
                let multiplier = ({
                    k: 1e3,
                    m: 1e6,
                    b: 1e9,
                    t: 1e12,
                } as Record<string, number>)[text.slice(-1).toLowerCase()];
                let number = parseFloat(text.slice(0, -1));
                if (!multiplier || Number.isNaN(number)) {
                    return NaN;
                }
                return number * multiplier;
            }
            return NaN;
        }
        item.users = convertUsers(item.users_string);
        // Rebuilt from scratch, never appended to. This function runs again on
        // every applyPlanLimits, and an append would give a card two of every
        // line the second time around.
        item.offer_list = [];

        // The grouped form ("50,000") is what the card and the dashboard meter
        // both print, so it is derived once here rather than in each place.
        item.email_sends_string = item.email_sends === null || item.email_sends === undefined
            ? "Unlimited"
            : groupThousands(Number(item.email_sends) || 0);

        // The newsletter subscriber ceiling, read the same way as the sends
        // above: a raw count from the server, grouped once here. null is the
        // Unlimited plan (plan.py holds float("inf"), which cannot be JSON, so
        // /plan publishes null) and also the blank built-in table.
        item.nlsub_string = item.nlsub === null || item.nlsub === undefined
            ? "Unlimited"
            : groupThousands(Number(item.nlsub) || 0);

        // A catalogue row's `limit` key -> [the number the "> 0" guard reads, the
        // string the line prints]. Both come from fields derived just above, so a
        // card line and the dashboard meter beside it cannot quote different
        // numbers for the same limit.
        const valueByLimit: { [limit: string]: [number, string] } = {
            users: [item.users, item.users_string],
            dbst: [item.storage_bytes.database, item.storage.database],
            cldst: [item.storage_bytes.cloud, item.storage.cloud],
            emlst: [item.storage_bytes.email, item.storage.email],
            hstst: [item.storage_bytes.host, item.storage.host],
            dbfst: [item.storage_bytes.file, item.storage.file],
            fpool: [item.storage_bytes.pool, item.storage.pool],
            egres: [item.egress_bytes, item.egress],
            emlsd: [Number(item.email_sends) || 0, item.email_sends_string],
            nlsub: [Number(item.nlsub) || 0, item.nlsub_string],
        };

        // The qualifying parts of a line move to a footnote on the upgrade page
        // (plan-copy.ts planCardNotes), keyed by the limit the line quotes. The
        // card itself stays a list of headline figures.
        const noteByLimit: { [limit: string]: string } = {
            fpool: "pool",
            emlst: "emlst",
            egres: "egres",
            emlsd: "emlsd",
        };

        for (const row of (planDisplayRows.length ? planDisplayRows : BUILT_IN_DISPLAY_ROWS)) {
            // hasOwnProperty, not a bare index: valueByLimit is an object
            // literal, so a row whose limit happens to name an inherited member
            // ("constructor", "toString", "__proto__") resolves to a FUNCTION,
            // passes the truthy guard below, and throws "pair is not iterable"
            // on the destructure. That throw escapes derivePlan, aborts the
            // applyPlanLimits loop part-way, and leaves every card after it on
            // the blank built-in table under a permanent pending row.
            const pair = Object.prototype.hasOwnProperty.call(valueByLimit, row.limit)
                ? valueByLimit[row.limit]
                : undefined;
            // A limit this build has no field for. A backend that gained a new
            // quota drops that line here rather than printing "undefined".
            if (!pair) continue;
            // A Skapi feature this dashboard does not offer (its mail and
            // newsletter pages are switched off) is not sold on its cards.
            if (CARD_HIDDEN_LIMITS.has(row.limit)) continue;

            const [value, text] = pair;
            // The "> 0" guard, per row, exactly as each hand-written push had it:
            // an unknown value (the blank built-in table, before GET /plan
            // answers) lands on NaN and an unlimited one on 0, and both mean "no
            // line", not "0B Database Storage".
            if (!(value > 0)) continue;

            // A served label can carry its own explanation in parentheses
            // ("File Storage (hosting, files and ...)"). The card prints the
            // name alone; the explanation is that line's footnote.
            item.offer_list.push({
                class: row.icon,
                text: `${text} ${row.label.replace(/\s*\([^)]*\)\s*$/, "")}`,
                note: noteByLimit[row.limit] || "",
            });
        }

        let label = item.label.toLowerCase();

        // A served ZERO for both newsletter limits (not null, which is unknown or
        // unlimited) means the plan has no newsletters at all: the backend refuses
        // both the subscription and the send. No plan carries that pair since
        // owner decision 12 of 2026-09-17 (Free sends 50 a month to 50
        // subscribers), so this only fires on a region still serving the older
        // Trial numbers.
        //
        // The newsletter rules (per-recipient counting, the per-owner cap on free
        // projects, the complaint shutoff) are policy, not plan limits, and are
        // not card lines: they are the footnote of the Monthly Email Sends line
        // (plan-copy.ts planCardNotes), and the meters link to the docs for them.
        if (item.email_sends === 0 && item.nlsub === 0 && !CARD_HIDDEN_LIMITS.has("nlsub")) {
            item.offer_list.push({
                class: "mail",
                text: "Newsletters not included",
                note: "",
            });
        }

        // if(!label.includes("free")) {
        //     item.offer_list.push({
        //         class: "setting",
        //         text: "Register Unlimited Client Secret Keys",
        //     });
        // }

        // The point of this line is the CONTRAST between the plans, which the
        // old wording left unsaid: plan.py's table reads "Past quota: hard stop
        // / hard stop / incremental" across Free, Standard and Premium. On a
        // plan without a hard stop nothing pauses when a limit is reached; the
        // excess is billed at the served rates instead. Say which one this is.
        //
        // A billed plan lists its overage rates on the card, one short line each
        // under this one (owner, 2026-09-19: the overage information stays on
        // the cards). A hard stop plan keeps one line; what the stop means (the
        // refusals, the suspension, the 30-day deletion) is its footnote on the
        // upgrade page.
        if (item.hard_stop === false) {
            item.offer_list.push({
                class: "billing",
                text: "No hard stop: overage billed monthly",
                note: "",
                rates: overageRateLines(overageRates, item.egress_bytes > 0),
            });
        } else {
            item.offer_list.push({
                class: "billing",
                text: "Hard stop at the plan limits",
                note: "stop",
            });
        }

        // Free and the perpetual licences carry no subscription, so a suspended
        // project on them can be deleted after 30 days; a project with a live
        // subscription never is. The hard stop footnote names these plans.
        item.deletable = item.price === 0 || String(item.label || "").includes("Perpetual");
    }
    return item;
}

/**
 * The plan table.
 *
 * REACTIVE, because the numbers below are only the fallback. The authority is
 * the backend's plan.py, served by `GET /plan`, and applyPlanLimits() overwrites
 * these once it answers. Components read this object directly (planCards.vue),
 * so a plain object would render the built-in numbers and never update.
 *
 * The built-ins stay for two reasons: the page renders before any request can
 * answer, and a failed request must not blank the pricing page.
 */
export const modelSpecs: any = reactive(((m: any) => {
    for (let k in m) derivePlan(m[k]);
    return m;
})({
    /* ── plan definitions ──────────────────────────────────────────────────
     *
     * EVERY LIMIT HERE IS DELIBERATELY BLANK. The numbers live in the backend's
     * plan.py, which is the thing that actually refuses an upload, and they
     * arrive through GET /plan (see loadPlanSpecs below). This file used to keep
     * a second hand-maintained copy, and the two drifted: the page quoted quotas
     * the server did not grant.
     *
     * So blank is not "missing", it is "not mine to state". A card renders its
     * price and description immediately and gains its quota lines when the
     * request answers; the `> 0` guards in derivePlan skip every line whose
     * value is still unknown, and getPercentUsed reads an unknown ceiling as no
     * bar rather than NaN.
     *
     * What IS authored here is what plan.py does not carry: price, description
     * and label. `hard_stop` is authored too, as the DEFAULT that matches
     * plan.py's hrdstp today (Free, Standard and the Standard licence stop;
     * Premium, its licence and Unlimited bill instead), because the billing
     * line prints before GET /plan answers; the served flag overwrites it.
     * Fill a limit back in and you have recreated the drift, because nothing
     * will ever tell you it disagrees with the server.
     */
    "Free": {
        price: 0,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: "",
            pool: ""
        },
        egress: "",
        email_sends: null,
        nlsub: null,
        description: "Perfect for testing and small projects. Free forever.",
        label: "Free",
        hard_stop: true
    },
    "Standard": {
        price: 19,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: "",
            pool: ""
        },
        egress: "",
        email_sends: null,
        nlsub: null,
        description: "Ideal for most projects. Perfect for hobby projects and learning.",
        label: "Standard",
        hard_stop: true
    },
    "Standard (Perpetual License)": {
        price: 299,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: "",
            pool: ""
        },
        egress: "",
        email_sends: null,
        nlsub: null,
        description: "Ideal for most projects. One-time payment with lifetime access. Perfect for hobby projects and learning.",
        label: "Standard (Perpetual)",
        hard_stop: true
    },
    "Premium": {
        price: 89,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: "",
            pool: ""
        },
        egress: "",
        email_sends: null,
        nlsub: null,
        description: "Perfect for businesses and serious projects with priority support.",
        label: "Premium",
        hard_stop: false
    },
    "Premium (Perpetual License)": {
        price: 1399,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: "",
            pool: ""
        },
        egress: "",
        email_sends: null,
        nlsub: null,
        description: "For businesses and projects that require more resources and priority support. One-time payment with lifetime access.",
        label: "Premium (Perpetual)",
        hard_stop: false
    },
    "Unlimited": {
        price: 599,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: "",
            pool: ""
        },
        egress: "",
        email_sends: null,
        nlsub: null,
        description: "For businesses and projects that require unlimited resources and priority support. Perfect for large-scale projects and enterprises.",
        label: "Unlimited",
        hard_stop: false
    }
}));


/* ── live plan limits ──────────────────────────────────────────────────────
 *
 * The table above is a fallback. The authority is the backend's plan.py, which
 * is what actually refuses an upload, and which this page has historically kept
 * a second hand-maintained copy of. Two copies with nothing keeping them in step
 * is how a page promises a quota the server rejects.
 */

/** Which table entries a served plan group governs.
 *
 * The perpetual-licence entries are not separate plans on the backend: groups 51
 * and 52 are defined as the Standard and Premium limits, so they track the same
 * numbers and would otherwise be the last hand-maintained copies left. */
const PLAN_KEYS_BY_GROUP: { [group: number]: string[] } = {
    1: ["Free"],
    2: ["Standard", "Standard (Perpetual License)"],
    3: ["Premium", "Premium (Perpetual License)"],
    50: ["Unlimited"],
};

/** Bytes back into this table's vocabulary ("500MB", "1GB", "1TB").
 *
 * The table stores human strings and derives its byte counts from them, so the
 * fetched number has to re-enter through the same door: writing storage_bytes
 * directly would leave the strings on the cards saying something else. `null`
 * from the server means no ceiling. */
function bytesToSpecString(bytes: number | null): string {
    if (bytes === null || !Number.isFinite(bytes as number)) return "Unlimited";
    if (bytes === 0) return "0B";

    const units: [string, number][] = [
        ["TB", 1024 ** 4], ["GB", 1024 ** 3], ["MB", 1024 ** 2], ["KB", 1024], ["B", 1],
    ];
    for (const [unit, size] of units) {
        if ((bytes as number) < size) continue;
        const value = (bytes as number) / size;
        // Whole numbers where they divide evenly ("1GB", not "1.00GB"), one
        // decimal otherwise, which is what storageUnitConverter reads back.
        return `${Number.isInteger(value) ? value : Number(value.toFixed(1))}${unit}`;
    }
    return `${bytes}B`;
}

/** Counts back into this table's vocabulary ("3", "50K", "Unlimited"). */
function countToSpecString(count: number | null): string {
    if (count === null || !Number.isFinite(count as number)) return "Unlimited";
    const n = count as number;
    if (n >= 1e9) return `${Number((n / 1e9).toFixed(1))}B`;
    if (n >= 1e6) return `${Number((n / 1e6).toFixed(1))}M`;
    if (n >= 1e3) return `${Number((n / 1e3).toFixed(1))}K`;
    return String(n);
}

type ServedPlan = {
    group: number;
    key: string;
    label: string;
    limits: {
        users: number | null;
        dbst: number | null;
        cldst: number | null;
        emlst: number | null;
        hstst: number | null;
        dbfst: number | null;
        egres?: number | null;
        emlsd?: number | null;
        // Newsletter subscribers held across every group of the service. A
        // lambda from before the limit existed sends none, so it is optional.
        nlsub?: number | null;
        // cldst + hstst + dbfst as ONE ceiling; only the overage meter reads it.
        fpool?: number | null;
        // false = bill past the limits, true (or absent, older lambdas) = stop.
        hrdstp?: boolean;
    };
};

/** The `overage` key of GET /plan. Optional: a cached or older response has none. */
type ServedOverage = {
    rates?: OverageRates;
    groups?: number[];
    bytes_per_gb?: number;
    billing?: string;
};

/** The `switches` key of GET /plan (payload v4). Optional: absent means both false. */
type ServedSwitches = {
    enforce_egress_hard_stop?: boolean;
    bill_egress_overage?: boolean;
};

/**
 * Overwrite the built-in numbers with the served ones.
 *
 * Prices, descriptions and labels are NOT touched: plan.py does not carry them,
 * so they stay authored here. Only the limits move.
 *
 * `display` is plan.py's PLAN_DISPLAY_ROWS: which limits a card prints, in what
 * order, under what label. Optional on purpose, see BUILT_IN_DISPLAY_ROWS.
 *
 * `switches` is plan.get_public_switches(). Absent (a cached or older response)
 * means both false, the defaults plan.py ships with.
 */
export function applyPlanLimits(plans: ServedPlan[], display?: PlanDisplayRow[] | null, overage?: ServedOverage | null, switches?: ServedSwitches | null): void {
    // Set BEFORE the loop, like the catalogues below: derivePlan reads the
    // billing switch when it prints the billing line. Only a literal true turns
    // a switch on.
    planSwitches.enforce_egress_hard_stop = switches?.enforce_egress_hard_stop === true;
    planSwitches.bill_egress_overage = switches?.bill_egress_overage === true;

    // Both catalogues are stored BEFORE the loop, because the derivePlan call
    // inside it is what builds offer_list from the display rows and prints the
    // billing line from the rates.
    if (Array.isArray(display) && display.length) {
        // Rows missing a field are dropped rather than drawn: a row with no
        // label would put "10GB undefined" on the pricing page. An empty result
        // leaves the built-in catalogue in place rather than blanking the cards.
        const usable = display.filter((row: any) => row
            && typeof row.limit === "string"
            && typeof row.label === "string"
            && typeof row.icon === "string");
        // Replaced wholesale, never merged: a row dropped on the backend has to
        // disappear from the cards, and merging would keep it alive forever.
        if (usable.length) planDisplayRows = usable;
    }

    // Same rule for the overage rates. Replaced wholesale, never
    // merged, so a metric dropped on the backend cannot linger at an old price;
    // an unusable payload leaves the built-in copy in place.
    if (overage && overage.rates && typeof overage.rates === "object") {
        const usable: OverageRates = {};
        for (const metric in overage.rates) {
            const rate: any = overage.rates[metric];
            if (rate && typeof rate.label === "string" && typeof rate.unit === "string"
                && typeof rate.cents_per_unit === "number") {
                // Absent billed means billed (a lambda from before the flag).
                usable[metric] = { ...rate, billed: rate.billed !== false };
            }
        }
        if (Object.keys(usable).length) overageRates = usable;
    }

    // Groups the payload carries that this build has no card for. A backend
    // that renumbers its public groups, or adds one, skips them below and the
    // affected cards silently keep their built-in numbers while the fetch still
    // counts as a success. Naming them turns "the Free card is empty" into one
    // console line instead of a network-tab hunt.
    const unmappedGroups: number[] = [];

    for (const plan of plans || []) {
        const keys = PLAN_KEYS_BY_GROUP[plan.group];
        if (!keys) unmappedGroups.push(plan.group);
        if (!keys || !plan.limits) continue;

        for (const key of keys) {
            const item = modelSpecs[key];
            if (!item) continue;

            item.users_string = countToSpecString(plan.limits.users);
            item.storage = {
                database: bytesToSpecString(plan.limits.dbst),
                cloud: bytesToSpecString(plan.limits.cldst),
                email: bytesToSpecString(plan.limits.emlst),
                host: bytesToSpecString(plan.limits.hstst),
                file: bytesToSpecString(plan.limits.dbfst),
                // A lambda from before fpool existed sends none; plan.py sets
                // fpool to the cldst figure, so that is the faithful fallback.
                pool: bytesToSpecString(plan.limits.fpool ?? plan.limits.cldst),
            };
            // Same door as the storage figures: write the authored form and let
            // derivePlan compute the bytes, so the card and the meters cannot end
            // up quoting different numbers.
            item.egress = bytesToSpecString(plan.limits.egres ?? null);
            item.email_sends = plan.limits.emlsd ?? null;
            // Same door again: the authored count in, the grouped string and the
            // card line out of derivePlan. Absent (an older lambda, or a cached
            // response) reads as no ceiling, which draws no line at all rather
            // than a wrong one.
            item.nlsub = plan.limits.nlsub ?? null;
            // Absent means a lambda from before the flag existed, and every
            // plan stopped hard then, so absent reads as true.
            item.hard_stop = plan.limits.hrdstp !== false;

            // Re-derive: storage_bytes, users and the whole offer list come from
            // the fields just replaced.
            derivePlan(item);
        }
    }

    if (unmappedGroups.length) {
        console.warn(
            `[plan] /plan served plan groups this build has no card for: ` +
            `${unmappedGroups.join(", ")}. Those cards keep their built-in ` +
            `limits. Known groups: ${Object.keys(PLAN_KEYS_BY_GROUP).join(", ")}`,
        );
    }
}

let planSpecsRequest: Promise<void> | null = null;

/**
 * Pull the enforced limits and apply them. Safe to call from anywhere; the
 * request happens once per page load and is answered from the browser cache for
 * a week after that (the endpoint sets the max-age, a client cannot).
 *
 * A PLAIN fetch with no custom headers on purpose. Anything non-safelisted turns
 * this into a preflighted request, and the gateway's allow-list is a fixed
 * string that would then have to be edited to match. It also keeps the response
 * cacheable, which the SDK's POST transport could not be.
 *
 * Failure is not an error: the built-in table is already on screen, so a dead
 * network leaves the page exactly as it was.
 */
/** True once a real /plan payload has been applied. Lets a view tell "the
 *  limits are still unknown" apart from "this plan genuinely has none". */
export const planSpecsLoaded = ref(false);

/**
 * Bumped whenever the SHAPE of the /plan payload a client needs changes (a new
 * key, a reshaped display catalogue). The response is cached by the browser
 * for a week (Cache-Control max-age=604800), keyed by url, so a visitor who
 * fetched /plan before a backend change keeps the old payload for up to seven
 * days; the cards then draw stale lines from it. A different query string is
 * a different cache entry, so a client build that needs the new shape gets it
 * on its first load. The backend ignores the parameter.
 *
 * 2: the display catalogue's pooled fpool line and the overage rates.
 * 3: the nlsub newsletter subscriber limit, its display row and its rate. A
 *    cached v=2 payload carries a display catalogue with no subscriber line and
 *    rates with no subscriber price, and it replaces the built-in copies of both
 *    wholesale, so without a new url that line stays missing for a week.
 * 4: plan policy v2 limits (bandwidth allowances, Trial nlsub 0), `billed` on
 *    every overage rate and the top-level `switches` key. Ship only after the
 *    region this build reads (prod us31, dev eu71) serves the V2 payload with
 *    switches, or the old payload is cached under v=4 for a week.
 *
 * 5: owner decision 11 of 2026-09-17, the Trial e-mail allowances (emlsd 100,
 *    emlst 50 MiB, nlsub 100) in BOTH the V1 and the V2 tables. A payload cached
 *    under v=4 still carries Trial 0/0/0, which draws "Newsletters not included"
 *    on the Free card and makes email storage one of Free's suspension limits for
 *    a week.
 * 6: owner decision 12 of 2026-09-17, which replaces those numbers where they
 *    differ: Free is users 50, emlsd 50, emlst 50 MiB, nlsub 50, again in BOTH
 *    tables. A payload cached under v=5 carries the decision 11 numbers, so the
 *    Free card would promise 100 sends to 100 subscribers for a week. Ship this
 *    build only after the region it reads (prod us31, dev eu71) serves the new
 *    numbers, or the old ones are cached under v=6 instead.
 *
 * The backend still serves the V1 limit numbers under this url while PLAN_V2_LIMITS
 * is False. Flipping it, or flipping enforce_egress_hard_stop or bill_egress_overage,
 * changes what this url answers, so that release bumps this to 7 in BOTH dashboards.
 * infra/PLAN_CHANGE_CHECKLIST.md section 4 is the list.
 */
const PLAN_PAYLOAD_VERSION = 8;

export function loadPlanSpecs(): Promise<void> {
    // Only a SUCCESSFUL load is remembered. Memoizing a failure was the trap:
    // one early miss (boot before the endpoint json resolved, an offline moment,
    // a poisoned cache entry) left every card blank for the whole page life with
    // no way to retry, because the failed promise satisfied this check forever.
    // Every exit is logged, including the two that do nothing. "I see no request"
    // is otherwise indistinguishable between "already loaded", "a call is still
    // in flight" and "this never ran at all", and only the last one is a bug.
    if (planSpecsRequest && planSpecsLoaded.value) {
        console.log("[plan] already loaded, no request needed", {
            standardLines: modelSpecs["Standard"].offer_list.length,
            premiumLines: modelSpecs["Premium"].offer_list.length,
        });
        return planSpecsRequest;
    }
    if (planSpecsRequest) {
        console.log("[plan] a request is already in flight, awaiting it");
        return planSpecsRequest;
    }

    planSpecsRequest = (async () => {
        try {
            console.log("[plan] loading plan limits", {
                mode: import.meta.env.VITE_MODE,
                service: import.meta.env.VITE_ADMIN,
            });

            // Imported HERE, not at the top of the file. This module is pulled in
            // by main.ts's own import graph (main -> user -> service ->
            // service-spec), so a static `import { ENDPOINTS } from "@/main"`
            // is read while main.ts is still evaluating and throws
            // "Cannot access 'ENDPOINTS' before initialization". A dynamic
            // import resolves after that evaluation finishes, which is exactly
            // when the endpoints actually exist.
            const { ENDPOINTS } = await import("@/main");
            const endpoints: any = await ENDPOINTS;
            const base = endpoints?.admin_public_endpoint;
            console.log("[plan] admin_public_endpoint =", base);
            if (!base) throw new Error("no admin endpoint");

            // Second attempt bypasses the browser cache. This response is held for
            // a week (max-age=604800), so a copy stored while the endpoint was
            // failing or absent would otherwise keep answering for days.
            const url = `${base}plan?v=${PLAN_PAYLOAD_VERSION}`;
            let applied = await attempt(url, "default");
            if (!applied) applied = await attempt(url, "reload");

            planSpecsLoaded.value = applied;
            if (applied) {
                console.log("[plan] limits applied", {
                    Standard: modelSpecs["Standard"].offer_list.map((o: any) => o.text),
                    Premium: modelSpecs["Premium"].offer_list.map((o: any) => o.text),
                });
            }
            if (!applied) {
                // Say WHICH backend was asked. VITE_MODE picks the admin service
                // and therefore the region: development -> eu71 (eu-west-1),
                // production -> us31 (us-east-1). /plan is served by the
                // PlanInfo function, which is not deployed in every region, so
                // naming the mode and the url turns "the cards are empty" into a
                // one-line answer instead of a network-tab hunt.
                console.warn(
                    `[plan] /plan returned nothing usable, cards will show no limits. ` +
                    `mode=${import.meta.env.VITE_MODE} service=${import.meta.env.VITE_ADMIN} url=${base}plan`,
                );
                planSpecsRequest = null; // let a later caller try again
            }
        } catch (err) {
            console.warn(
                `[plan] could not load live plan limits; using built-in table ` +
                `(mode=${import.meta.env.VITE_MODE}, service=${import.meta.env.VITE_ADMIN})`,
                err,
            );
            planSpecsRequest = null;
        }
    })();

    return planSpecsRequest;
}

async function attempt(url: string, cache: RequestCache): Promise<boolean> {
    try {
        console.log(`[plan] GET ${url} (cache: ${cache})`);
        const res = await fetch(url, { cache });
        console.log(`[plan] <- ${res.status} ${res.statusText}`);
        if (!res.ok) return false;
        const body = await res.json();
        console.log("[plan] payload", body);
        if (!body || !Array.isArray(body.plans) || !body.plans.length) {
            console.warn("[plan] payload had no plans array");
            return false;
        }
        // `body.display` and `body.overage` are absent from a lambda that predates
        // them, and from any response already sitting in a visitor's week-long
        // cache. Passing them undefined is not a failure: applyPlanLimits keeps
        // the built-in catalogue and the built-in rates, so the cards still list
        // every line including dbfst and still price the billing line. An absent
        // `body.switches` reads as both switches off.
        applyPlanLimits(body.plans, body.display, body.overage, body.switches);
        return true;
    } catch (err) {
        console.warn(`[plan] fetch failed (cache: ${cache})`, err);
        return false;
    }
}

// Kicked off on import. Every consumer of this module renders plan numbers, and
// the fetch is one cached GET a week.
void loadPlanSpecs();
